import { type RefObject, useEffect, useRef } from 'react';
import { safeSessionStorage } from '@toss/storage';
import { useRouter } from 'next/router';

const SCROLL_POSITION_KEY_PREFIX = 'scroll-position:';
/**
 * 데이터가 늦게 와도 복원되도록 재시도하는 프레임 수(60fps 기준 ≈3초). 돌아온 직후엔 내용이
 * 스켈레톤 높이라 위치가 잘린다. 실측이 아니라 넉넉히 잡은 값이고, 복원되면 바로 멈춘다.
 */
const RESTORE_MAX_FRAMES = 180;

type ScrollRestorationOptions = {
  /** 스크롤 대상. 없으면 window. 내부 div 가 스크롤하는 화면은 next 의 복원(window 전용)이 안 돼 넘긴다. */
  target?: RefObject<HTMLElement | null>;
  enabled?: boolean;
};

function readPosition(target: ScrollRestorationOptions['target']) {
  if (!target) return window.scrollY;
  return target.current?.scrollTop ?? null;
}

function getMaxScrollTop(target: ScrollRestorationOptions['target']) {
  if (!target) {
    return document.documentElement.scrollHeight - window.innerHeight;
  }
  const el = target.current;
  return el ? el.scrollHeight - el.clientHeight : null;
}

function scrollTargetTo(
  target: ScrollRestorationOptions['target'],
  top: number,
) {
  if (!target) {
    window.scrollTo(0, top);
    return;
  }
  if (target.current) target.current.scrollTop = top;
}

function readSaved(key: string) {
  const saved = safeSessionStorage.get(key);
  // "0" 도 저장값이므로 null 체크로 구분한다.
  if (saved == null) return null;
  const top = parseInt(saved, 10);
  return Number.isNaN(top) ? null : top;
}

/**
 * 스크롤 위치를 라우트(asPath)별로 기억했다가 `shouldRestore`(보통 뒤로/앞으로가기)일 때 되돌린다.
 * `target` 이 있고 복원하지 않으면 그 element 를 맨 위로 보낸다 — window 는 next 가 맡는다.
 * 클래스 상세와 좌측 메뉴 옆 카드(ProductContentFrame)가 함께 쓴다.
 */
export const useScrollRestoration = (
  isDataReady: boolean,
  shouldRestore: boolean,
  { target, enabled = true }: ScrollRestorationOptions = {},
) => {
  const router = useRouter();
  const key = `${SCROLL_POSITION_KEY_PREFIX}${router.asPath}`;
  const hasRestored = useRef(false);

  useEffect(() => {
    hasRestored.current = false;
  }, [key]);

  // 스크롤할 때와 떠날 때(routeChangeStart 는 언마운트 전이라 위치가 살아 있다) 저장한다.
  useEffect(() => {
    // target 은 데이터가 준비돼야 그려질 수 있어, 준비 뒤에 스크롤 리스너를 단다.
    if (!enabled || !isDataReady) return;
    let ticking = false;
    const save = () => {
      const top = readPosition(target);
      if (top != null) safeSessionStorage.set(key, String(top));
    };
    const saveOnScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        save();
        ticking = false;
      });
    };
    const scrollSource = target ? target.current : window;
    scrollSource?.addEventListener('scroll', saveOnScroll, { passive: true });
    router.events.on('routeChangeStart', save);
    // 새 탭·탭 닫기처럼 라우터 이벤트가 안 도는 이탈도 덮는다.
    window.addEventListener('pagehide', save);
    return () => {
      scrollSource?.removeEventListener('scroll', saveOnScroll);
      router.events.off('routeChangeStart', save);
      window.removeEventListener('pagehide', save);
    };
  }, [enabled, isDataReady, key, router.events, target]);

  useEffect(() => {
    if (!enabled || !isDataReady || hasRestored.current) return;

    const saved = shouldRestore ? readSaved(key) : null;
    if (saved == null) {
      if (target?.current) target.current.scrollTop = 0;
      hasRestored.current = true;
      return;
    }

    let frame = 0;
    let rafId = 0;
    const tryRestore = () => {
      const maxTop = getMaxScrollTop(target);
      // 목표만큼 스크롤할 수 있게 되면(또는 더 기다려도 안 되면) 한 번 넣고 끝낸다.
      if ((maxTop != null && maxTop >= saved) || frame >= RESTORE_MAX_FRAMES) {
        scrollTargetTo(target, saved);
        hasRestored.current = true;
        return;
      }
      frame += 1;
      rafId = requestAnimationFrame(tryRestore);
    };
    rafId = requestAnimationFrame(tryRestore);
    return () => cancelAnimationFrame(rafId);
  }, [enabled, isDataReady, shouldRestore, key, target]);
};
